import { useEffect, useMemo, useState } from 'react';
import { FusionDropDownInput, FusionInputField } from 'components';
import layout from 'data/SystemTypeForm.json';
import BaseModal from 'components/Modals/BaseModal';
import { Button, Form } from 'react-bootstrap';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { useSelector } from 'react-redux';

import {
  selectSystemTypeMap,
  selectDeviceTypeMap,
  selectSystemCount,
  selectIntegrations,
} from 'features/integrations/state/selectors/integrationSelectors';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';

export default function SystemModal({ onSubmit, onCancel, system: systemToEdit, loading }) {
  const systemTypeMap = useSelector(selectSystemTypeMap);
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const systemCount = useSelector(selectSystemCount);
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();

  // Normalized list of system type names for dropdowns and lookups
  const systemTypes = useMemo(() => {
    return (systemTypeMap || []).map((s) =>
      typeof s === 'string' ? s : s?.system_type || s?.systemType || '',
    );
  }, [systemTypeMap]);

  const [formData, setFormData] = useState(() => {
    if (!systemToEdit) return {};
    const idx = (systemTypes || []).findIndex((name) => name === systemToEdit.system_type);
    return {
      ...systemToEdit,
      system_type: idx >= 0 ? '' + idx : '-1',
      system_name: systemToEdit.system_name,
    };
  });

  // If systemTypes arrives async, keep edit form stable (optional safety)
  useEffect(() => {
    if (!systemToEdit) return;
    const idx = (systemTypes || []).findIndex((name) => name === systemToEdit.system_type);
    setFormData((prev) => ({
      ...prev,
      ...systemToEdit,
      system_type: idx >= 0 ? '' + idx : '-1',
      system_name: systemToEdit.system_name,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemTypes]);

  const renderField = (field) => {
    switch (field.type) {
      case 'input-field':
        return (
          <FusionInputField
            label={field.label}
            value={formData[field['data-field']] || ''}
            onChange={(value) => setFormData({ ...formData, [field['data-field']]: value })}
          />
        );
      default:
        return null;
    }
  };

  const isAtSystemLimit = 0 >= (systemCount?.advanced_system || 0);

  const canSubmit = () => {
    const selectedSystemName = systemTypes?.[parseInt(formData?.system_type)];
    const layoutItem = layout[selectedSystemName?.toLowerCase()];

    const required = layoutItem
      ? ['system_type', 'system_name', ...(layoutItem['required-fields'] || [])]
      : ['system_type', 'system_name'];

    return !required.map((key) => !!formData[key]).includes(false);
  };

  const submit = () => {
    const selectedSystemType = systemTypes?.[parseInt(formData.system_type)];
    onSubmit({
      ...formData,
      system_type: selectedSystemType,
      device_type: (deviceTypeMap || [])
        .filter((device) => device.system_type === selectedSystemType)
        .map((device) => device.id),
    });
  };

  return (
    <BaseModal
      show={true}
      handleShow={onCancel}
      title={systemToEdit ? 'Edit System' : 'New System'}
    >
      <Form>
        <div className="d-flex flex-column gap-3">
          <FusionDropDownInput
            label="System Type"
            options={systemTypes || []}
            value={formData?.system_type || -1}
            onChange={(value) => {
              setFormData({
                ...formData,
                system_type: value,
                email: null,
                password: null,
              });
            }}
            disabled={!!systemToEdit}
          />

          <FusionInputField
            label="System Name"
            value={formData?.system_name || ''}
            onChange={(value) => setFormData({ ...formData, system_name: value })}
          />

          {formData?.system_type &&
            (() => {
              const selectedSystemName = systemTypes?.[parseInt(formData?.system_type)];
              const layoutKey = selectedSystemName ? selectedSystemName.toLowerCase() : null;
              const layoutItem = layoutKey ? layout[layoutKey] : null;

              return (
                layoutItem?.components?.map((field, index) => (
                  <div key={`${field['data-field']}-${index}`}>{renderField(field)}</div>
                )) || null
              );
            })()}

          <div className="fw-bold fs-6">
            <p className="mb-0">Available Licenses:</p>
            <div>{`${integrations?.length || 0} of ${
              systemCount?.advanced_system || 0
            } (Licenses remaining)`}</div>
          </div>
        </div>

        {onSubmit && (
          <div className="d-flex justify-content-sm-end justify-content-center">
            <Button
              disabled={!canSubmit() || isAtSystemLimit}
              className="border-0 mt-3 py-2 px-4 rounded-4 w-100 w-sm-auto"
              onClick={submit}
              variant="primary"
            >
              {systemToEdit ? 'Submit' : 'Connect'}
            </Button>
          </div>
        )}
      </Form>

      {loading && (
        <div>
          <WidgetLoadingAnimation disableText />
        </div>
      )}
    </BaseModal>
  );
}
